import type { Session } from './session';

/**
 * Where the back-office session token is kept: **`sessionStorage`**, under one
 * key, read and written here and nowhere else.
 *
 * This is the decision FE005 exists to make, so what follows is the argument
 * rather than the conclusion. There are three options and none of them is free.
 *
 * **`localStorage`** survives a reload, a crash and a browser restart, and is
 * shared by every tab on the origin. On a back-office desk that is precisely
 * the problem: the token outlives the browsing session, so somebody who closes
 * the window and walks away leaves a working bearer token on disk for whatever
 * is left of its twelve hours, and it is still there in the morning.
 *
 * **Memory only** — what the FE004 placeholder did — cannot be read off disk at
 * all, but a reload signs you out mid-task, and it buys less than it appears
 * to: a script injected into this origin reads an in-memory token too, by
 * reaching the store that holds it or the request that carries it. Memory
 * defends against *persistence*, not against injection.
 *
 * **`sessionStorage`** is the one taken. It survives a reload, which is the
 * thing that actually happens daily, and it is scoped to the tab — closing the
 * tab takes the token with it, and a second tab starts signed out.
 *
 * What that trades away, stated rather than glossed:
 *
 * - **Injection reads it.** An injected script reads `sessionStorage` exactly
 *   as it reads `localStorage`. What this choice buys over `localStorage` is
 *   reach and duration — one tab, until that tab closes — not immunity. The
 *   defences that matter against injection are elsewhere: a content-security
 *   policy, and never writing the token to a log, a URL or an error report.
 * - **A second tab is a second sign-in.** A freshly opened tab has its own
 *   empty `sessionStorage`, so the user signs in again and the server issues a
 *   second session with its own twelve-hour ceiling. Signing out in one tab
 *   does not sign out the other; that tab keeps working until the server ends
 *   it. Cross-tab sign-out would need `localStorage` and its `storage` event,
 *   which is the option rejected above.
 * - **A duplicated tab is not a fresh one.** Chrome's "Duplicate tab" and an
 *   opener-linked `window.open` copy `sessionStorage` into the new tab, token
 *   included. This module cannot close that; it is worth knowing before
 *   assuming one tab means one session.
 * - **What comes back out is trusted only until the next request.** The server
 *   re-checks both bounds on every call, so a restored token that is already
 *   dead answers `AUTH_SESSION_EXPIRED` and clears itself. The client is not
 *   the enforcer here and does not pretend to be.
 *
 * Not a cookie: the API authenticates with `Authorization: Bearer`, so a cookie
 * would have to be read by script to be sent anywhere — that is `localStorage`
 * with extra steps and none of `HttpOnly`'s protection. A real `HttpOnly`
 * cookie would have to be set and accepted by the API, and it does neither.
 */

/**
 * One key, prefixed with the app's name so a shared origin cannot collide with
 * it. It holds the sign-in response verbatim; nothing else is written here.
 */
const KEY = 'blossom.session';

/**
 * Persistence, behind three methods, so the holder above it never learns which
 * `Storage` it got or whether there was one at all.
 */
export interface SessionStorage {
  read(): Session | null;
  write(session: Session): void;
  clear(): void;
}

/**
 * The browser's per-tab storage, or `null` where there is none.
 *
 * Reading the property itself throws — not just writing to it — when the
 * browser blocks storage for the origin, so the access is guarded rather than
 * the use. A browser that refuses storage still gets a working back-office;
 * it just signs out on reload, which is the honest consequence.
 */
export function browserSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * What must be true of a stored value before this app will act on it.
 *
 * Only the three fields this module itself depends on are checked, and the
 * rest of the sign-in response is not re-described here — restating an API
 * shape by hand is exactly what `packages/api-client` exists to prevent. The
 * cast is honest for a narrower reason too: whatever is in this key was put
 * there by this app, and the token is proven or refused by the server on the
 * very next request regardless of what this function believed.
 */
function parse(raw: string): Session | null {
  let value: unknown;

  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;

  const candidate = value as Partial<Session>;

  if (typeof candidate.token !== 'string' || candidate.token === '') return null;
  if (typeof candidate.expiresAt !== 'string') return null;
  if (typeof candidate.user !== 'object' || candidate.user === null) return null;

  return candidate as Session;
}

/** A storage that keeps nothing, for a browser that permits nothing. */
const noStorage: SessionStorage = {
  read: () => null,
  write: () => {},
  clear: () => {},
};

export function createSessionStorage(backing: Storage | null): SessionStorage {
  if (!backing) return noStorage;

  return {
    read() {
      try {
        const raw = backing.getItem(KEY);

        return raw === null ? null : parse(raw);
      } catch {
        // Storage went away mid-session — a quota policy, a private window, an
        // extension. Not being able to read it is the same as it being empty.
        return null;
      }
    },

    write(session) {
      try {
        backing.setItem(KEY, JSON.stringify(session));
      } catch {
        // A failed write must not fail the sign-in that produced it. The
        // session stays in memory for the life of the page; the only thing
        // lost is surviving a reload. Nothing is logged — the value that
        // could not be written is the token.
      }
    },

    clear() {
      try {
        backing.removeItem(KEY);
      } catch {
        // Nothing further to try. The in-memory session is dropped by the
        // caller either way, so this browser is signed out even if the key
        // outlives it — and the server has been told, or was the one that
        // told us.
      }
    },
  };
}
